import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AppDownloadBanner } from "./components/AppDownloadPrompt.tsx";
import { Suspense, lazy, useEffect, useState } from "react";
import HomeLayout from "./pages/HomeLayout.tsx";
import StartPage from "./pages/StartPage.tsx";
import { SequenceToast } from "./feed/components/ui/SequenceToast.tsx";
import { FeedKnownWordToast } from "./feed/components/ui/FeedKnownWordToast.tsx";
import { savedPhraseToast$ } from "./feed/state/videoState.ts";
import { PhoneCard } from "./components/PhoneCard.tsx";
import { UpgradeProvider } from "./context/UpgradeContext.tsx";
import useVersionChecker from "./hooks/useVersionChecker.ts";
import { cleanupLocalStorageWeekly } from "./lib/cleanup.ts";
import parrotdemo from "./assets/suset-onboarding-720p.mp4";
import iphone from "./assets/iphone.png";
import { fetchAndCacheAsset } from "@/lib/cache";

// Route-level code splitting: the shell + home tabs stay eager for fast first
// paint; everything else loads on navigation.
const OnboardingPage = lazy(() => import("./pages/OnboardingPage.tsx"));
const NameInputPage = lazy(
  () => import("./components/Onboarding/NameInputPage.tsx")
);
const CourseInfoPage = lazy(() => import("./pages/CourseInfoPage.tsx"));
const ChapterPathPage = lazy(() => import("./pages/ChapterPathPage.tsx"));
const ConstructionPage = lazy(() => import("./pages/ConstructionPage.tsx"));
const LoginPage = lazy(() => import("./pages/LoginPage.tsx"));
const SignUpPage = lazy(() => import("./pages/SignUpPage.tsx"));
const Lesson = lazy(() => import("./pages/Lesson.tsx"));
const TermsOfService = lazy(() => import("./pages/Terms.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const SupportPage = lazy(() => import("./pages/SupportPage.tsx"));
const RedirectPage = lazy(() => import("./pages/RedirectPage.tsx"));
const PushControl = lazy(() => import("./components/PushControl.tsx"));
const IAPCard = lazy(() => import("./components/AppleIAPCard.tsx"));
const PremiumPage = lazy(() => import("./pages/PremiumPage.tsx"));
const StripeCheckout = lazy(() => import("./pages/StripeCheckout.tsx"));
const CheckoutComplete = lazy(() => import("./pages/CheckoutComplete.tsx"));
const ReviewPage = lazy(() => import("./pages/ReviewPage.tsx"));
const UpgradePage = lazy(() => import("./pages/UpgradePage.tsx"));
const PlacementTestPage = lazy(() => import("./pages/PlacementTestPage.tsx"));
const ErrorPage = lazy(() => import("./pages/ErrorPage.tsx"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage.tsx"));
const MethodExplanationPage = lazy(
  () => import("./pages/MethodExplanationPage.tsx")
);
const FallbackPaywallPage = lazy(
  () => import("./pages/FallbackPaywallPage.tsx")
);
const FlightSchoolPage = lazy(() => import("./pages/FlightSchoolPage.tsx"));
const LevelUpPage = lazy(() => import("./pages/LevelUpPage.tsx"));
const ConversationPage = lazy(() => import("./pages/ConversationPage.tsx"));
const TutorialDemoPage = lazy(() => import("./pages/TutorialDemoPage.tsx"));

const AppHomePath = "/home";

// Prefetches the onboarding demo video (2.5 MB) + iphone frame only when the
// onboarding route actually mounts, so logged-in users never pay for it.
function OnboardingRoute() {
  const [parrotDemoUrl, setParrotDemoUrl] = useState<string>(parrotdemo);
  const [iphoneUrl, setIphoneUrl] = useState<string>(iphone);

  useEffect(() => {
    // Object URLs created from the prefetched blobs are revoked on unmount so
    // they don't leak for the document lifetime.
    let cancelled = false;
    const createdObjectUrls: string[] = [];
    const fetchAssets = async () => {
      const parrotDemoBlob = await fetchAndCacheAsset(parrotdemo);
      if (cancelled) return;
      const iphoneBlob = await fetchAndCacheAsset(iphone);
      if (cancelled) return;
      if (!parrotDemoBlob || !iphoneBlob) {
        console.warn("Failed to prefetch assets");
        return;
      }
      const parrotDemoObjectUrl = URL.createObjectURL(parrotDemoBlob);
      const iphoneObjectUrl = URL.createObjectURL(iphoneBlob);
      // If the user left onboarding while the fetches were in flight, cleanup
      // already ran with an empty list — revoke these immediately and don't
      // setState on the unmounted component.
      if (cancelled) {
        URL.revokeObjectURL(parrotDemoObjectUrl);
        URL.revokeObjectURL(iphoneObjectUrl);
        return;
      }
      createdObjectUrls.push(parrotDemoObjectUrl, iphoneObjectUrl);
      setParrotDemoUrl(parrotDemoObjectUrl);
      setIphoneUrl(iphoneObjectUrl);
    };
    fetchAssets();
    return () => {
      cancelled = true;
      createdObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  return (
    <OnboardingPage
      assetSources={{
        parrotDemo: parrotDemoUrl,
        iphone: iphoneUrl,
      }}
    />
  );
}

// Route-aware mount for the iOS "download the app" banner. Lives inside the
// Router so it can read the current pathname; the banner itself decides whether
// to render (iOS-web only, allowed routes only, not previously dismissed).
function RouteAwareAppDownloadBanner() {
  const { pathname } = useLocation();
  return <AppDownloadBanner pathname={pathname} />;
}

function App() {
  const queryParams = new URLSearchParams(location.search);
  const backUrl = queryParams.get("back");

  useVersionChecker();

  useEffect(() => {
    // Keys are `lesson_{id}_activities_{count}` (generateActivitiesForLesson);
    // the `(_\d+)?` also catches any legacy `lesson_{id}_activities` entries.
    cleanupLocalStorageWeekly(/^lesson_\d+_activities(_\d+)?$/);
  }, []);

  return (
    <UpgradeProvider>
      <div className="App max-w-[100vw]">
        {/* BASE_URL is "/app/" (droplet-embedded) or "/" (standalone
            app.parrotapp.com); basename + bad-auth redirect follow it. */}
        <Router basename={import.meta.env.BASE_URL.replace(/\/+$/, "") || "/"}>
          <RouteAwareAppDownloadBanner />
          <Suspense fallback={null}>
            <Routes>
              <Route
                path="/"
                element={
                  backUrl ? (
                    <Navigate to={backUrl} />
                  ) : (
                    <PhoneCard white>
                      <StartPage />
                    </PhoneCard>
                  )
                }
              />
              <Route path="/redirect" element={<RedirectPage />} />
              <Route
                path="/terms/*"
                element={
                  <PhoneCard>
                    <TermsOfService />
                  </PhoneCard>
                }
              />
              <Route
                path="/privacy/*"
                element={
                  <PhoneCard>
                    <Privacy />
                  </PhoneCard>
                }
              />
              <Route
                path="/support"
                element={
                  <PhoneCard>
                    <SupportPage />
                  </PhoneCard>
                }
              />
              <Route path="/placement" element={<PlacementTestPage />} />
              <Route path="/premium/*" element={<PremiumPage />} />
              <Route path="/checkout/*" element={<StripeCheckout />} />
              <Route path="/complete/*" element={<CheckoutComplete />} />
              <Route path="/iap" element={<IAPCard />} />
              <Route path="/push" element={<PushControl />} />
              <Route
                path="/login"
                element={
                  <PhoneCard white>
                    <LoginPage />
                  </PhoneCard>
                }
              />
              <Route
                path="/signup"
                element={
                  <PhoneCard white>
                    <SignUpPage />
                  </PhoneCard>
                }
              />
              <Route path="/upgrade" element={<UpgradePage />} />
              <Route
                path="/onboarding/*"
                element={
                  <PhoneCard>
                    <OnboardingRoute />
                  </PhoneCard>
                }
              />
              <Route
                path="/name"
                element={
                  <PhoneCard>
                    <NameInputPage />
                  </PhoneCard>
                }
              />
              <Route
                path="/review"
                element={
                  <PhoneCard>
                    <ReviewPage />
                  </PhoneCard>
                }
              />
              <Route
                path="/error"
                element={
                  <PhoneCard>
                    <ErrorPage />
                  </PhoneCard>
                }
              />
              <Route path={AppHomePath + "/*"} element={<HomeLayout />}></Route>
              <Route path="/course/:courseId" element={<CourseInfoPage />} />
              <Route
                path="/reset-password"
                element={
                  <PhoneCard>
                    <ResetPasswordPage />
                  </PhoneCard>
                }
              />
              <Route
                path="/method-explanation"
                element={
                  <PhoneCard>
                    <MethodExplanationPage />
                  </PhoneCard>
                }
              />
              <Route path="/fallback-paywall" element={<FallbackPaywallPage />} />
              <Route path="/flight-school" element={<FlightSchoolPage />} />
              <Route path="/level-up" element={<LevelUpPage />} />
              <Route
                path="/conversation"
                element={
                  <PhoneCard>
                    <ConversationPage />
                  </PhoneCard>
                }
              />
              <Route
                path="/tutorial/demo/:demoId"
                element={<TutorialDemoPage />}
              />
              <Route path="/chapter/:chapterId" element={<ChapterPathPage />} />
              <Route
                path="/construction"
                element={
                  <PhoneCard>
                    <ConstructionPage />
                  </PhoneCard>
                }
              />
              <Route
                path="/chapter/:chapterId/lesson/:lessonId/*"
                element={<Lesson />}
              />
            </Routes>
          </Suspense>
          {/* App-level confirmation toasts — mounted here so they outlive the
              translation popup / word panel that triggers them. */}
          <SequenceToast
            seq$={savedPhraseToast$}
            message="You saved this phrase"
            viewTo="/home/dictionary?contentType=phrases&viewMode=learning"
          />
          <FeedKnownWordToast />
        </Router>
      </div>
    </UpgradeProvider>
  );
}

export default App;
